/**
 * Reading an organization's trial, for the admin list and the detail screen.
 *
 * Pure on purpose: every rule here is one a test can pin without rendering anything, and the two
 * screens that show trial state have to agree about all of them.
 *
 * **Trials are notification only.** An expired trial does not pause an organization and does not
 * take anything away — it is a date the platform sends notices about. Pausing is a separate,
 * manual system-admin action. Nothing here should ever imply otherwise.
 */

import { dateTimeFormatter } from '../../utils/dateTimeParts';
import type { OrganizationTrialFields, TrialStatus } from '../../types/admin';

const KNOWN_STATUSES: readonly string[] = [
  'none',
  'active',
  'expiring',
  'expired',
  'converted',
];

/**
 * The organization's trial status, as the server banded it.
 *
 * **The band is never recomputed here.** The server decides where `active` becomes `expiring`, and
 * that boundary is tuned to the notice schedule; a day threshold copied into the client is how the
 * badge comes to disagree with the email the coach already received.
 *
 * Anything unrecognised reads as `none`, which covers both an API too old to send the field at all
 * and one that has grown a band this build has never heard of. Showing no trial state is the
 * honest answer in both cases — inventing one would put a word on screen that nothing supports.
 */
export const trialStatusOf = (organization: OrganizationTrialFields): TrialStatus => {
  const status = organization.trialStatus;
  return typeof status === 'string' && KNOWN_STATUSES.includes(status)
    ? (status as TrialStatus)
    : 'none';
};

/**
 * Whether the organization has a trial window recorded, whatever the server called its band.
 *
 * Deliberately independent of `trialStatusOf`: the dates are the thing that actually exists, and
 * asking "does it have a window" through a band that collapses anything unrecognised to `none` is
 * how a panel comes to say "not on a trial" above two real dates.
 */
export const hasTrialWindow = (organization: OrganizationTrialFields): boolean =>
  !!organization.trialStartedAt || !!organization.trialEndsAt;

/**
 * The word the status badge shows for each band.
 *
 * `none` and `converted` are both null, meaning the badge falls through to its ordinary "Active".
 * Neither is a state of the *organization*: one never had a trial and the other is a paying
 * customer, and both are simply active. The conversion date is on the detail screen, and the list
 * carries it in its own Trial column.
 */
export const TRIAL_BADGE_WORD: Record<TrialStatus, string | null> = {
  none: null,
  active: 'Trial',
  expiring: 'Trial ending',
  expired: 'Trial ended',
  converted: null,
};

/**
 * "Aug 1, 2026", formatted in UTC.
 *
 * These are instants, and the ones this screen writes are UTC midnight of the day an admin picked.
 * Formatting them in the reader's own zone would show a system admin west of UTC the day before
 * the one they typed. Returns null for a missing or unparseable value so a caller renders a dash
 * rather than "Invalid Date".
 */
export const formatTrialDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;

  return dateTimeFormatter('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(at);
};

/**
 * "12 days left", from the server's own count.
 *
 * Only meaningful while a trial is running, so callers show it for `active` and `expiring` only:
 * the API sends 0 for `expired`, and "0 days left" beside "Trial ended" says the same thing twice
 * in a way that reads like something is about to happen. Null for anything not countable.
 */
export const trialDaysRemainingLabel = (
  days: number | null | undefined
): string | null => {
  if (typeof days !== 'number' || !Number.isFinite(days) || days < 0) return null;
  if (days === 0) return 'Last day';
  return days === 1 ? '1 day left' : `${days} days left`;
};

/**
 * The Trial column in the admin organizations list, as one plain string.
 *
 * A string rather than markup so the whole rule set is testable without rendering a table, and so
 * the cell stays a cell. Every branch degrades to a dash rather than to "Invalid Date" or an empty
 * box, because a list row must never depend on trial data an older API has never heard of.
 */
export const trialCellText = (organization: OrganizationTrialFields): string => {
  const status = trialStatusOf(organization);

  if (status === 'none') return '-';

  if (status === 'converted') {
    const on = formatTrialDate(organization.trialConvertedAt);
    return on ? `Converted ${on}` : 'Converted';
  }

  const ends = formatTrialDate(organization.trialEndsAt);

  if (status === 'expired') return ends ? `Ended ${ends}` : 'Ended';

  const days = trialDaysRemainingLabel(organization.trialDaysRemaining);
  if (ends && days) return `${ends} (${days})`;
  return ends ?? days ?? '-';
};

/** Which call was refused, so the copy can name the thing the admin was actually doing. */
export type TrialAction = 'set' | 'clear' | 'convert';

/**
 * ApiError carries the status code as a plain property. Read structurally rather than with
 * `instanceof`, so a test that mocks the service module and throws a plain object still narrows
 * the way a real refusal does.
 */
const statusCodeOf = (error: unknown): number => {
  const thrown = (error ?? {}) as { statusCode?: unknown };
  return typeof thrown.statusCode === 'number' ? thrown.statusCode : 0;
};

/**
 * The api client falls back to "API request failed: 409 Conflict" when a body carried no message.
 * That is a developer's string, not something to put in front of anyone.
 */
const serverMessage = (error: unknown): string | null => {
  const thrown = (error ?? {}) as { message?: unknown };
  const message = typeof thrown.message === 'string' ? thrown.message.trim() : '';
  return message && !message.startsWith('API request failed') ? message : null;
};

const CONFLICT: Record<TrialAction, string> = {
  set: 'This organization has already converted, so its trial dates cannot be changed. Clear the trial first if you need to put it back on one.',
  clear:
    'The trial could not be cleared because this organization has already converted. Reload the page to see where it stands.',
  convert: 'This organization is already marked as converted.',
};

const FALLBACK: Record<TrialAction, string> = {
  set: 'The trial dates could not be saved. Please try again.',
  clear: 'The trial could not be cleared. Please try again.',
  convert: 'This organization could not be marked as converted. Please try again.',
};

/**
 * What to tell a system admin about a refused trial call.
 *
 * Each refusal gets its own sentence because each one needs a different thing done about it. A 400
 * means the dates are wrong and are still on screen to fix; a 409 means conversion has closed the
 * door on editing and names the way back through; a 404 means the organization is gone and no
 * amount of retrying will help. One shared "something went wrong" would send an admin round the
 * same loop for all three.
 */
export const trialFailureMessage = (error: unknown, action: TrialAction): string => {
  switch (statusCodeOf(error)) {
    case 400:
      return (
        serverMessage(error) ??
        'Enter both dates, and make sure the trial ends after it starts.'
      );
    case 404:
      return 'This organization is no longer there. Go back to the list and open it again.';
    case 409:
      /*
        One sentence per action, because "already converted" means something different on each.
        On a set it is the reason the dates were refused and clearing is the way through. On a
        clear that same sentence would tell an admin to do the thing they just tried, so it says
        what happened instead. Convert is idempotent and should never reach here at all.
      */
      return CONFLICT[action] ?? serverMessage(error) ?? FALLBACK[action];
    default:
      return serverMessage(error) ?? FALLBACK[action];
  }
};
